import { fabric } from 'fabric';
import get from 'lodash-es/get';
import pick from 'lodash-es/pick';
import set from 'lodash-es/set';
import keyboard from '../keyboard';
import variables from '../variables';
import { UPLOAD_STATE_UPLOADING } from '../upload/constants';
import { Matrix } from '../lib/matrix';
import Color from 'color';

const canvasEventProxy = (listener, _this) => o => {
  if (_this.canvas) {
    listener.call(_this, o);
  }
};

const MODE_IDLE = 'idle';
const MODE_CROPPING = 'cropping';

const filterProperties = variables.filters;
const ownProperties = filterProperties.concat();

const LudusImage = fabric.util.createClass(fabric.Group, {
  type: 'ludusImage',
  lockScalingFlip: true,
  lockSkewingX: true,
  lockSkewingY: true,
  cropMode: 'auto',
  rx: 0,
  ry: 0,

  initialize(image, options = {}) {
    set(options, 'top', options.top || 0);
    set(options, 'left', options.left || 0);

    this._initialized = false;
    this._mode = MODE_IDLE;
    this._hasImage = !!image;
    this._originalWidth = get(options, 'ludus.originalWidth') || 0;
    this._originalHeight = get(options, 'ludus.originalHeight') || 0;

    this._limitedWidth =
      get(options, 'ludus.limitedWidth') || this._originalWidth;
    this._limitedHeight =
      get(options, 'ludus.limitedHeight') || this._originalHeight;
    const optionsOverrides = {};
    let left = options.imageCropX * (options.imageScaleX || 1);
    let top = options.imageCropY * (options.imageScaleY || 1);

    options = LudusImage.migrateCropCoords(options);
    // Migrate crop of 2.0 to 3.0
    // In 2.0 coords of crop where based on fabric logic from crop area TL to image TL.
    // negative value mean empty padding not accepted in 3.0 => we always fill the crop area
    if (
      options.ludus &&
      options.ludus.ludusImageVersion === '2.0' &&
      (options.imageCropX !== undefined || options.imageCropY !== undefined)
    ) {
      left = this._limitedWidth / 2 - options.imageCropX - options.width / 2;
      top = this._limitedHeight / 2 - options.imageCropY - options.height / 2;
      options.imageCropX = left;
      options.imageCropY = top;
    }

    const imageOptions = {
      originX: 'center',
      originY: 'center',
      cropX: 0,
      cropY: 0,
      left,
      top,
      width: this._limitedWidth,
      height: this._limitedHeight,
      scaleX: options.imageScaleX || 1,
      scaleY: options.imageScaleY || 1,
    };

    // Handle Fabric version <= 2.0 (version was not specified before)
    // where width & height could be changed without scaling
    if (
      options.width &&
      options.height &&
      typeof options.version === 'undefined'
    ) {
      optionsOverrides.scaleX =
        ((options.scaleX || 1) * options.width) / this._originalWidth;
      optionsOverrides.scaleY =
        ((options.scaleY || 1) * options.height) / this._originalHeight;
      optionsOverrides.width = this._originalWidth;
      optionsOverrides.height = this._originalHeight;
      imageOptions.width = this._originalWidth;
      imageOptions.height = this._originalHeight;
    } else {
      if (options.width) {
        imageOptions.width = options.width / (options.imageScaleX || 1);
      }
      if (options.height) {
        imageOptions.height = options.height / (options.imageScaleY || 1);
      }
    }

    const { type, thumbnail } = options.ludus;
    let { format, url } = options.ludus;

    if (type === 'video') {
      format = 'jpg';
      url = thumbnail || options.src;
    }

    const params = {
      ...options.ludus,
      format,
      url,
    };

    this._image = new fabric.ImageAsync(
      image,
      imageOptions,
      params,
      LudusImage.getImageFromParams,
    );
    this._image.setCoords();

    const children = [this._image];
    this.callSuper('initialize', children, {
      ...options,
      ...optionsOverrides,
      hasControls: false,
    });

    this._updateFilters(true);
    this.on('removed', this._onRemove.bind(this));
    this.on('mousedblclick', this._onMouseDoubleClick.bind(this));
    this.on('scaling', this._onScaling.bind(this));
    this._canvasMouseDownHandler = canvasEventProxy(
      this._onCanvasMouseDown,
      this,
    );
    this._canvasMouseUpHandler = canvasEventProxy(this._onCanvasMouseUp, this);
    this._canvasMouseMoveHandler = canvasEventProxy(
      this._onCanvasMouseMove,
      this,
    );

    this._initialized = true;
  },

  reloadImage(callback) {
    LudusImage.getImageFromParams(
      {
        ...this.ludus,
        isRemote: true,
      },
      image => {
        this._image.initImageRender(image);
        this.setImageCoords();
        callback && callback();
      },
    );
  },

  replaceImage(data) {
    this.ludus = {
      ...this.ludus,
      ...data,
    };

    this._originalWidth = data.originalWidth;
    this._originalHeight = data.originalHeight;

    this._limitedWidth = data.limitedWidth;
    this._limitedHeight = data.limitedHeight;
    this.set({
      imageCropX: 0,
      imageCropY: 0,
      cropHeight: this._limitedHeight,
      cropWidth: this._limitedWidth,
    });
    this.reloadImage(() => {
      this.canvas.fire('object:modified', { target: this });
    });
    this.dirty = true;
  },

  refresh(url) {
    fabric.util.loadImage(
      url,
      image => {
        this._image.initImageRender(image);
        this.setImageCoords();
      },
      null,
      'Anonymous',
    );
  },

  _addCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    // Be sure to remove all existing listeners before adding new ones
    this._removeCanvasListeners();
    this.canvas.on('mouse:down', this._canvasMouseDownHandler);
    this.canvas.on('mouse:up', this._canvasMouseUpHandler);
    this.canvas.on('mouse:move', this._canvasMouseMoveHandler);
  },

  _removeCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    this.canvas.off('mouse:down', this._canvasMouseDownHandler);
    this.canvas.off('mouse:up', this._canvasMouseUpHandler);
    this.canvas.off('mouse:move', this._canvasMouseMoveHandler);
  },

  _getNonTransformedDimensions: function() {
    var w = this.width || 200;
    var h = this.height || 200;
    return { x: w, y: h };
  },

  getCorner(key) {
    let corner = this.__corner || this.lastCorner;
    if (this.__corner) {
      this.lastCorner = this.__corner;
    }

    if (!corner && key) {
      if (key === 'height') {
        corner = 'mb';
      }
      if (key === 'width') {
        corner = 'mr';
      }
    }
    return corner;
  },

  setImageCoords() {
    const originalDimensions = this._image.getOriginalSize();
    const oWidth = this._limitedWidth || originalDimensions.width;
    const oHeight = this._limitedHeight || originalDimensions.height;
    const w = this.cropWidth
      ? this.width * (this.imageScaleX || 1)
      : this.width;
    const h = this.cropHeight
      ? this.height * (this.imageScaleY || 1)
      : this.height;

    const cropWidth = this.imageScaleX ? this.cropWidth || this.width : oWidth;
    const cropHeight = this.imageScaleY
      ? this.cropHeight || this.height
      : oHeight;
    const cropRatio = cropWidth / cropHeight;
    const ratio = w / h;
    const scale = ratio > cropRatio ? w / cropWidth : h / cropHeight;

    const finalScale = scale;
    this._image.scaleX = finalScale;
    this._image.scaleY = finalScale;

    this.checkImagePosition(
      this.imageCropX * finalScale,
      this.imageCropY * finalScale,
    );
  },

  _set(key, value) {
    if (!this._initialized) {
      this.callSuper('_set', key, value);
      return this;
    }

    if (key === 'rx' || key === 'ry') {
      const maxBorderRadius = this.getMaxBorderRadius();
      value = Math.min(value, maxBorderRadius);
      this.dirty = true;
      this.canvas.requestRenderAll();
    }

    if (key === 'width' || key === 'height') {
      const originalDimensions = this._image.getOriginalSize();
      const oWidth = originalDimensions.width; // 1920; // this.ludus.originalWidth;
      const oHeight = originalDimensions.height; // 1280; // this.ludus.originalHeight;

      if (!this.isCropping()) {
        this.callSuper('_set', key, Math.max(1, value));
        this.setImageCoords();
        return this;
      }
      if (this.isCropping()) {
        this._image.scaleX = this._imageScaleX;
        this._image.scaleY = this._imageScaleY;
        let max = value;
        let maxY = value;
        const corner = this.getCorner(key);
        const dirX = this.flipX ? -1 : 1;
        const dirY = this.flipY ? -1 : 1;
        const cropX = this.imageCropX || 0;
        const cropY = this.imageCropY || 0;
        // TO DO IMPLEMENT SIGN (if flipped image)
        if (corner.includes('r')) {
          max =
            (oWidth / 2) * this._imageScaleX + cropX * dirX + this.width / 2;
        }
        if (corner.includes('l')) {
          max =
            (oWidth / 2) * this._imageScaleX - cropX * dirX + this.width / 2;
        }

        if (corner.includes('b')) {
          maxY =
            (oHeight / 2) * this._imageScaleY + cropY * dirY + this.height / 2;
        }
        if (corner.includes('t')) {
          maxY =
            (oHeight / 2) * this._imageScaleY - cropY * dirY + this.height / 2;
        }

        key === 'width' && this.callSuper('_set', key, Math.min(value, max));
        key === 'height' && this.callSuper('_set', key, Math.min(value, maxY));

        this._updateScale(key);
        !this.__corner && this._storeCurrentState(); // we are applying via props not mouse, se we must trigger it
        return true;
      }
    }

    this.callSuper('_set', key, value);
    if (filterProperties.includes(key)) {
      this._updateFilters();
    }

    return this;
  },

  _setMode(mode) {
    if (!this.isEditable()) {
      return;
    }
    this._mode = mode;
    this._updateMode();
  },

  _updateMode() {
    if (this.isCropping()) {
      this._addCanvasListeners();
      this._storePreviousState();
      this._idleProps = pick(this, [
        'cornerColor',
        'hasRotatingPoint',
        'lockUniScaling',
        'lockMovementX',
        'lockMovementY',
        'shadow',
        'skewX',
        'skewY',
        'globalCompositeOperation',
      ]);

      this.set({
        skipCache: true,
        cornerColor: this.cornerStrokeColor,
        hasRotatingPoint: false,
        skewX: 0,
        skewY: 0,
        lockUniScaling: false,
        lockMovementX: true,
        lockMovementY: true,
        shadow: null,
        globalCompositeOperation: 'source-over',
        _imageScaleX: this._image.scaleX,
        _imageScaleY: this._image.scaleY,
      });
      this.setCoords();

      this.setControlsVisibility({
        mt: true,
        mb: true,
        ml: true,
        mr: true,
      });
    } else {
      this._removeCanvasListeners();
      this.set(this._idleProps);
      this.setCoords();
      this.dirty = true;

      this.set({
        skipCache: false,
        ...(this.hasNewChanges() && {
          imageScaleX: this._image.scaleX,
          imageScaleY: this._image.scaleY,
          imageCropX: this._image.left / this._image.scaleX,
          imageCropY: this._image.top / this._image.scaleY,
          cropWidth: this.width,
          cropHeight: this.height,
        }),
      });
    }
    this._storeCurrentState();
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  },

  _storeCurrentState() {
    this._currentWidth = this.width;
    this._currentHeight = this.height;
    this._currentImageLeft = this._image.left;
    this._currentImageTop = this._image.top;
  },

  _storePreviousState() {
    this._previousWidth = this.width;
    this._previousHeight = this.height;
    this._previousImageScaleX = this._image.scaleX;
    this._previousImageScaleY = this._image.scaleY;
    this._previousImageTop = this._image.top;
    this._previousImageLeft = this._image.left;
  },

  _restorePreviousState() {
    this.set({
      width: this._previousWidth,
      height: this._previousHeight,
    });
    this._image.set({
      left: this._previousImageLeft,
      top: this._previousImageTop,
      scaleX: this._previousImageScaleX,
      scaleY: this._previousImageScaleY,
    });

    this.setCoords();
    this._image.setCoords();
  },

  resetCrop() {
    this.cancelCroppingMode();

    this.set({
      width: this._originalWidth,
      height: this._originalHeight,
      left: 0,
      top: 0,
      cropMode: 'auto',
      lockUniScaling: false,
      scaleX: 1,
      scaleY: 1,
      imageScaleX: 1,
      imageScaleY: 1,
      imageCropX: 0,
      imageCropY: 0,
      cropWidth: this._originalWidth,
      cropHeight: this._originalHeight,
    });
    const imageOptions = {
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      cropX: 0,
      cropY: 0,
      width: this._originalWidth,
      height: this._originalHeight,
    };
    this._image.set(imageOptions);

    this.setObjectsCoords();
    this._storeCurrentState();
    this._updateCrop();
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
    this.setCoords();
  },

  isEditable() {
    return this._hasImage;
  },

  isCopyEnabled() {
    return !this.isCropping();
  },

  canApplyFilters() {
    return true;
  },

  isCropping() {
    return this._mode === MODE_CROPPING;
  },

  hasNewChanges() {
    return (
      this._previousWidth !== this.width ||
      this._previousHeight !== this.height ||
      this._previousImageLeft !== this._image.left ||
      this._previousImageTop !== this._image.top ||
      this._previousImageScaleX !== this._image.scaleX ||
      this._previousImageScaleY !== this._image.scaleY
    );
  },

  hasCropChanges() {
    return (
      this.width !== this._originalWidth ||
      this.height !== this._originalHeight ||
      this.cropX !== 0 ||
      this.cropY !== 0
    );
  },

  enterCroppingMode() {
    if (
      !this.isCropping() &&
      !this.ludus.locked &&
      this.ludus.format !== 'svg'
    ) {
      this._setMode(MODE_CROPPING);
      this.canvas.fire('image:croppingEntered', { target: this });
    }
  },

  applyCroppingMode() {
    if (this.isCropping()) {
      this._setMode(MODE_IDLE);
      this.canvas.fire('object:modified', { target: this });
      this.canvas.fire('image:croppingExited', { target: this });
      return true;
    }
    return false;
  },

  cancelCroppingMode() {
    if (this.isCropping()) {
      this._restorePreviousState();
      this._setMode(MODE_IDLE);
      this.canvas.fire('image:croppingExited', { target: this });
      return true;
    }
    return false;
  },

  _onRemove() {
    this._removeCanvasListeners();
  },

  onDeselect(e) {
    if (e.e) {
      this.applyCroppingMode();
    } else {
      this.cancelCroppingMode();
    }
    return false; // return false to allow deselection
  },

  // Ludus ObjectSelection implementation
  // Return true or false to tell whether the keystroke was handled or not
  onKeyDownWithKey(key) {
    if (key.is(keyboard.ESCAPE)) {
      return this.cancelCroppingMode();
    }
    if (key.is(keyboard.ENTER)) {
      return this.applyCroppingMode();
    }
    if (key.is(keyboard.SHIFT)) {
      this.shiftKeyActive = true;
      return true;
    }
    return false;
  },

  // Ludus ObjectSelection implementation
  // Return true or false to tell whether the keystroke was handled or not
  onKeyUpWithKey(key) {
    if (key.is(keyboard.SHIFT)) {
      this.shiftKeyActive = false;
      return true;
    }
    return false;
  },

  _onMouseDoubleClick(e) {
    const ev = e.e;

    // make sure there is not secondary action happening with keyboard shorcut
    if (!ev.shiftKey && !ev.altKey && !ev.metaKey) {
      this.enterCroppingMode();
    }
  },

  _onScaling() {
    if (!this.isCropping()) {
      return;
    }
    // Reset dragging coords so it cancels the drag logic started by
    // the onMouseDown handler when starting scaling
    this._startDragCoords = null;
  },

  _updateScale(fromKey) {
    const offsetX = this._currentWidth - this.width;
    const offsetY = this._currentHeight - this.height;
    const corner = this.getCorner(fromKey);

    const sign = corner && corner.includes('r') ? 1 : -1;
    const signTop = corner && corner.includes('b') ? 1 : -1;

    const dirX = this.flipX ? -1 : 1;
    const dirY = this.flipY ? -1 : 1;

    this._image.left =
      (this._currentImageLeft || 0) + (offsetX * sign * dirX) / 2;
    this._image.top =
      (this._currentImageTop || 0) + (offsetY * signTop * dirY) / 2;

    this._updateCrop();
  },

  _updateCrop() {
    this.cropX = this._image.left / this._image.scaleX;
    this.cropY = this._image.top / this._image.scaleY;
    this.imageCropX = this._image.left;
    this.imageCropY = this._image.top;
    this.cropWidth = this.width / this._image.scaleX;
    this.cropHeight = this.height / this._image.scaleY;
    this.imageScaleX = this._image.scaleX;
    this.imageScaleY = this._image.scaleY;
  },

  _updateFilters(skipAtInit) {
    if (!this.isEditable()) {
      return;
    }
    this._applyFiltersToImage(this._image, skipAtInit);
  },

  _applyFilterValue(obj, index, prop, value) {
    if (obj.filters[index]) {
      obj.filters[index][prop] = value;
      return obj.filters[index];
    }
    if (!obj.filters[index] && value !== 0) {
      switch (prop) {
        case 'blur':
          return new fabric.Image.filters.Blur({ blur: value });
        case 'matrix':
          return new fabric.Image.filters.ColorMatrix({ matrix: value });
        default:
          return null;
      }
    } else {
      return null;
    }
  },

  _applyFiltersToImage(image, skipAtInit = false) {
    const c = this.contrast ? this.contrast / 100 + 1 : 1;
    const s = this.saturation ? this.saturation / 100 + 1 : 1;
    const b = this.brightness ? this.brightness / 100 + 1 : 1;
    const t = this.tint
      ? Color(this.tint)
          .rgb()
          .array()
      : null;
    const blur = this.blur || 0;
    const hasColorChanges = c !== 1 || s !== 1 || b !== 1 || this.invert || t;

    // prettier-ignore
    const brightnessM = [
      b, 0, 0, 0, 0,
      0, b, 0, 0, 0,
      0, 0, b, 0, 0,
      0, 0, 0, 1, 0,
    ];

    const ct = (1 - c) / 2;

    // prettier-ignore
    const contrastM = [
      c, 0, 0, 0, ct,
      0, c, 0, 0, ct,
      0, 0, c, 0, ct,
      0, 0, 0, 1, 0,
    ];

    image.filters[0] = this.blur
      ? this._applyFilterValue(image, 0, 'blur', blur)
      : null;
    image.filters[2] = this.saturation
      ? this._applyFilterValue(
          image,
          2,
          'matrix',
          LudusImage.buildColorMatrix(b, c, s, t),
        )
      : null;
    image.filters[3] = this.brightness
      ? this._applyFilterValue(image, 3, 'matrix', brightnessM)
      : null;
    image.filters[4] = this.contrast
      ? this._applyFilterValue(image, 4, 'matrix', contrastM)
      : null;
    image.filters[5] = this.invert ? new fabric.Image.filters.Invert() : null;

    // tint color
    if (t) {
      const color = `rgb(${t.toString()})`;
      const alpha = Color(color).alpha();
      if (image.filters[1]) {
        image.filters[1].color = color;
        image.filters[1].alpha = alpha;
      } else {
        image.filters[1] = new fabric.Image.filters.BlendColor({
          color,
          alpha,
          mode: 'tint',
        });
      }
    } else {
      image.filters[1] = null;
    }

    // actually apply filter if filters exist
    if (!skipAtInit || hasColorChanges || this.blur) {
      this.dirty = true;
      image.applyFilters();
    }
  },

  _onCanvasMouseDown(o) {
    // Warning: this handler is also called when grabbing down scaling controls
    if (!this.isCropping()) {
      return;
    }
    if (o.target && o.target.__corner === 0) {
      this.isMouseDown = true;
    }
    this._startDragCoords = { x: 0, y: 0 };
    this.previousPointer = fabric.util.getPointer(o.e);
  },

  getMinZoom() {
    const minScaleX = this.width / this._image.width;
    const minScaleY = this.height / this._image.height;
    return Math.max(minScaleX, minScaleY);
  },

  checkImagePosition(left, top) {
    const _left = left || this._image.left;
    const _top = top || this._image.top;
    const w = this._originalWidth * this._image.scaleX - this.width;
    const h = this._originalHeight * this._image.scaleY - this.height;
    const maxLeft = w / 2;
    const minLeft = -w / 2;
    const maxTop = h / 2;
    const minTop = -h / 2;
    this._image.left = Math.max(Math.min(_left, maxLeft), minLeft);
    this._image.top = Math.max(Math.min(_top, maxTop), minTop);
    // if you pass a left and top you'll have to store the currentImageLeft manually
    // ie: if moving mouse you don't want to store the positions at each frame only on mouse up.
    if (left === undefined || top === undefined) {
      this._currentImageLeft = this._image.left;
      this._currentImageTop = this._image.top;
    }
  },

  scaleImage(value) {
    this._image.scaleX = value;
    this._image.scaleY = value;
    this._imageScaleX = value;
    this._imageScaleY = value;
    this.imageScaleX = value;
    this.imageScaleY = value;
    this.checkImagePosition();
    this.canvas.requestRenderAll();
  },

  _onCanvasMouseMove(o) {
    // avoid doing anything is not mousedown

    if (!this.isCropping() || !this.isMouseDown) {
      return;
    }

    if (!this._startDragCoords) {
      return;
    }
    const pointer = fabric.util.getPointer(o.e);
    const movement = {
      x:
        (pointer.x - this.previousPointer.x) /
        this.scaleX /
        this.canvas.getZoom(),
      y:
        (pointer.y - this.previousPointer.y) /
        this.scaleY /
        this.canvas.getZoom(),
    };
    this.previousPointer = pointer;
    let snap = false;

    this._startDragCoords.x += movement.x;
    this._startDragCoords.y += movement.y;
    const newPosX = (this._image.left || 0) + movement.x;
    const newPosY = (this._image.top || 0) + movement.y;

    if (this.shiftKeyActive) {
      if (
        Math.abs(this._startDragCoords.x) > Math.abs(this._startDragCoords.y)
      ) {
        if (Math.abs(this._startDragCoords.x) > 10) {
          snap = 'y';
        }
      } else {
        if (Math.abs(this._startDragCoords.y) > 10) snap = 'x';
      }
    }

    const dirX = this.flipX ? -1 : 1;
    const dirY = this.flipY ? -1 : 1;

    const computedLeft = snap === 'x' ? this._image.left || 0 : newPosX * dirX;
    const computedTop = snap === 'y' ? this._image.top || 0 : newPosY * dirY;
    this.checkImagePosition(computedLeft, computedTop);
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  },

  _onCanvasMouseUp(o) {
    if (!this.isCropping()) {
      return;
    }
    this.isMouseDown = false;
    this.previousPointer = null;
    this._storeCurrentState();
    this._deltaMove = null;
    this._startDragCoords = null;
  },

  getMaxBorderRadius() {
    const maxRx = (this.width * this.scaleX) / 2;
    const maxRy = (this.height * this.scaleY) / 2;
    const maxR = Math.min(maxRx, maxRy);
    return Math.max(maxR, 0);
  },

  // Override fabric.Group drawing to hide backgroundImage when exporting image data
  drawObject(ctx) {
    const originalDimensions = this._image.getOriginalSize();

    const oWidth = originalDimensions.width; // 1920; // this.ludus.originalWidth;
    const oHeight = originalDimensions.height; // 1280; // this.ludus.originalHeight;

    this._image.width = originalDimensions.width; // this.width / this.scaleX;
    this._image.height = originalDimensions.height; // this.height / this.scaleY;

    const transformMatrix = this.calcTransformMatrix();
    const matrix = new Matrix(transformMatrix);
    const options = matrix.decompose();
    const w = this.width;
    const h = this.height;

    if (this.canvas && this.canvas.interactive && this.isCropping()) {
      const elementToDraw = this._image._element;
      if (elementToDraw) {
        ctx.globalAlpha = 0.5;
        ctx.drawImage(
          elementToDraw,
          (-oWidth * this._image.scaleX) / 2 + (this._image.left || 0),
          (-oHeight * this._image.scaleY) / 2 + (this._image.top || 0),
          elementToDraw.width * this._image.scaleX,
          elementToDraw.height * this._image.scaleY,
        );
        ctx.globalAlpha = 1;
      }
    }

    let rx = Math.abs((this.rx || 0) / options.scale.x); // Math.abs to avoid negative rx with flip
    let ry = Math.abs((this.ry || 0) / options.scale.y);
    const sw = w * options.scale.x;
    const sh = h * options.scale.y;
    if (rx > w / 2) {
      rx = sw / 2 / options.scale.x;
      ry = sw / 2 / options.scale.y;
    }
    if (ry > h / 2) {
      ry = sh / 2 / options.scale.y;
      rx = sh / 2 / options.scale.x;
    }
    ctx.fillStyle = 'red';

    ctx.beginPath();
    ctx.roundedRectangle(-w * 0.5, -h * 0.5, w, h, rx, ry);
    ctx.closePath();
    ctx.shadowBlur = 0;
    ctx.shadowColor = null;
    ctx.clip();

    this._image.render(ctx);
    // ctx.restore();
  },

  render(ctx) {
    if (this._image && (!this.width || !this.height)) {
      ctx.save();
      this.transform(ctx);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
      ctx.fillRect(-100, -100, 200, 200);
      ctx.restore();
    } else {
      this.callSuper('render', ctx);
    }
  },

  toObject(propertiesToInclude = []) {
    const object = this.callSuper(
      'toObject',
      propertiesToInclude.concat([
        ...ownProperties,
        'cropMode',
        'rx',
        'ry',
        'imageCropX', // in version 1 of ludusImage it's negative value from the top left corner (* this.scaleX)
        'imageCropY',
        'cropWidth',
        'cropHeight',
        'imageScaleX',
        'imageScaleY',
      ]),
    );

    delete object.objects;

    let overrides = {};
    if (this.isCropping()) {
      // We don't want to save cropping state, so we serialize the normal idle state instead
      overrides = this._idleProps;
    }

    const obj = {
      ...object,
      ...overrides,
      tint: this.tint,
      imageCropX: this.imageCropX,
      imageCropY: this.imageCropY,
      imageScaleX: this.imageScaleX,
      imageScaleY: this.imageScaleY,
      cropWidth: this.cropWidth,
      cropHeight: this.cropHeight,
      strokeDashArray: 0,
    };
    // Store actual image dimensions in the case of a fetched image,
    // for which we didn't have the upload dimensions
    if (!get(obj, 'ludus.originalWidth')) {
      set(obj, 'ludus.originalWidth', this._originalWidth);
    }
    if (!get(obj, 'ludus.originalHeight')) {
      set(obj, 'ludus.originalHeight', this._originalHeight);
    }

    // Store actual image limited dimensions in the case it had to be reduced to work in the editor
    set(obj, 'ludus.limitedWidth', this._limitedWidth);
    set(obj, 'ludus.limitedHeight', this._limitedHeight);
    set(obj, 'ludus.ludusImageVersion', '4.0');

    return obj;
  },
});

// prettier-ignore
LudusImage.buildBalanceMatrix = (b, c) => [
  c, 0, 0, 0, b,
  0, c, 0, 0, b,
  0, 0, c, 0, b,
  0, 0, 0, 1, 0,
];

LudusImage.buildColorMatrix = (
  brightness,
  contrast,
  saturation,
  tint,
  invert = false,
) => {
  // how to build a Matrix https://docs.rainmeter.net/tips/colormatrix-guide/
  // used by Editor & viewer

  const s = saturation;
  const c = 1;
  const b = 1;
  const ta = 0;
  const tr = ta * (tint ? tint[0] / 255 : 0);
  const tg = ta * (tint ? tint[1] / 255 : 0);
  const tb = ta * (tint ? tint[2] / 255 : 0);
  const i = invert ? -1 : 1;
  const ict = i * c * (1 - ta);
  const ip = 0;
  const t = (1.0 - c) / 2.0;
  const lr = 0.3086;
  const lg = 0.6094;
  const lb = 0.082;
  const sr = (1 - s) * lr;
  const sg = (1 - s) * lg;
  const sb = (1 - s) * lb;
  const srs = sr + s;
  const sgs = sg + s;
  const sbs = sb + s;

  const colorMatrix = [
    (ict * srs * b).toFixed(4),
    (ict * sg).toFixed(4),
    (ict * sb).toFixed(4),
    0,
    (tr + ip + t).toFixed(4),
    (ict * sr).toFixed(4),
    (ict * sgs * b).toFixed(4),
    (ict * sb).toFixed(4),
    0,
    (tg + ip + t).toFixed(4),
    (ict * sr).toFixed(4),
    (ict * sg).toFixed(4),
    (ict * sbs * b).toFixed(4),
    0,
    (tb + ip + t).toFixed(4),
    0,
    0,
    0,
    1,
    0,
  ];
  return colorMatrix;
};

LudusImage.fromObject = (object, callback) => {
  const options = fabric.util.object.clone(object, true);

  const { type, thumbnail } = options.ludus;
  let { format, url } = options.ludus;

  if (type === 'video') {
    format = 'jpg';
    url = thumbnail || options.src;
  }

  const params = {
    ...options.ludus,
    format,
    url,
  };

  if (params.highQuality) {
    params.url = params.url.replace('q_auto:eco', 'q_auto:best');
  }

  if (callback) {
    callback(new LudusImage(params.url, options));
  }
};
LudusImage.getImageFromParams = (params, callback, isRemote) => {
  if (params.uploadState && params.uploadState === UPLOAD_STATE_UPLOADING) {
    fabric.util.loadImage(params.originalUrl, callback, null, 'Anonymous');
  } else {
    fabric.util.loadImage(params.url, callback, null, 'Anonymous');
  }
};

/**
 * As the edition will require the dispatching of a redux action, we will let an action creator
 * (see imagesActions.initializeImageManager) register it.
 *
 * @param f
 */
LudusImage.registerImageManager = f => {
  LudusImage.getImageFromParams = f;
};

LudusImage.MODE_IDLE = MODE_IDLE;
LudusImage.MODE_CROPPING = MODE_CROPPING;

LudusImage.migrateCropCoords = _options => {
  if (_options.ludus?.ludusImageVersion !== '4.0' && _options.cropWidth) {
    _options.cropWidth = _options.cropWidth * (_options.imageScaleX || 1);
    _options.cropHeight = _options.cropHeight * (_options.imageScaleY || 1);
  }
  if (_options.imageScaleX && (!_options.cropWidth || !_options.cropHeight)) {
    _options.cropWidth = _options.width;
    _options.cropHeight = _options.height;
  }
  return _options;
};

export default LudusImage;
